import { ArrowsLeftRight, ChatCircleDots } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import type { RootState } from '@/redux/store';
import { isInvoicingSubscription, usePlanChangeAvailability } from '@/features/plan-change';
import { useOpenSupportChat } from '@/shared/support/supportChat';
import { invoicingPlanLabelOf } from '../constants/planFeatures';
import { INVOICE_V2_ROUTES } from '../constants/routes';
import {
  PLAN_CHANGE_SUPPORT_ENTRY,
  PLAN_CHANGE_UNAVAILABLE,
  planChangeSupportDraft,
  type InvoiceQuota,
} from '../lib/quota';
import { InvoiceButton } from './InvoiceButton';

/**
 * The honest answer to a user who bought an invoicing plan and wants another one
 * (`isPlanChangeUnavailable`): one line and «Habla con nosotros», which opens the
 * support chat with the plan already in the draft. Every surface that would offer
 * an upgrade shows this instead.
 *
 * Once the engine lets this user change the plan in place (t-298), it says so and
 * opens the plans screen, where the upgrade is a prorated plan change.
 */
export const PlanChangeUnavailable = ({
  plan,
  targetPlan,
  variant = 'primary',
  fullWidth,
  className,
}: {
  plan: InvoiceQuota['plan'];
  /** The plan the user is after, when the surface knows it (the upsell of a feature). */
  targetPlan?: string;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  className?: string;
}) => {
  const openSupportChat = useOpenSupportChat();
  const navigate = useNavigate();
  const invoicingSubscription = useSelector((state: RootState) =>
    state.profile.activeSuscriptions.find((sub) => isInvoicingSubscription(sub)),
  );
  const { available: canChangePlan } = usePlanChangeAvailability(invoicingSubscription?.id);

  if (canChangePlan) {
    return (
      <div className={cn('flex flex-col gap-3', fullWidth ? 'items-stretch' : 'items-start', className)}>
        <p className='text-h-text-heading text-sm leading-snug'>
          Sube de plan cuando quieras: hoy pagas solo los días que faltan de tu periodo.
        </p>
        <InvoiceButton
          variant={variant}
          fullWidth={fullWidth}
          iconLeft={<ArrowsLeftRight size={16} weight='bold' />}
          onClick={() => navigate(INVOICE_V2_ROUTES.PLANS)}>
          Cambiar de plan
        </InvoiceButton>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', fullWidth ? 'items-stretch' : 'items-start', className)}>
      <p className='text-h-text-heading text-sm leading-snug'>{PLAN_CHANGE_UNAVAILABLE}.</p>
      <InvoiceButton
        variant={variant}
        fullWidth={fullWidth}
        iconLeft={<ChatCircleDots size={16} weight='fill' />}
        onClick={() =>
          openSupportChat(
            PLAN_CHANGE_SUPPORT_ENTRY,
            planChangeSupportDraft(invoicingPlanLabelOf(plan), targetPlan),
          )
        }>
        Habla con nosotros
      </InvoiceButton>
    </div>
  );
};
